// An organization in PostHog (D58, APS-469), on its page's side column: how far its funnel went
// and when its console was last open (its members' page views under /o/<id>), and the product
// flags that reach it, as one key-value list, with the Flags tab in the section's right slot.
// Each part says on its own when PostHog is not set up. A read that fails is one line for the
// section, where the first failed part would be, naming what could not be read (APS-502), with
// one retry for every failed read; a sign-in or permission error keeps the generic state.
import { ErrorState as ErrorLine, KV, Section } from '@apsio/ui'
import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { isApiError } from '../api/client'
import { orgActivityQuery, orgProductFlagsQuery } from '../api/queries'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { FlagState } from './ProductFlags'
import { PostHogSource } from './posthog'

const STEP: Record<'created' | 'trial' | 'first_session' | 'paid', MessageId> = {
  created: 'org.posthog.step.created',
  trial: 'org.posthog.step.trial',
  first_session: 'org.posthog.step.firstSession',
  paid: 'org.posthog.step.paid',
}

/** The section's one failure line: what could not be read, by which reads failed. */
function ReadFailed({ failed }: { failed: { funnel: UseQueryResult; flags: UseQueryResult } }) {
  const { t } = useI18n()
  const reads = [failed.funnel, failed.flags].filter((q) => q.isError)
  const signIn = reads.find((q) => isApiError(q.error, 401) || isApiError(q.error, 403))
  if (signIn) return <ErrorState error={signIn.error} />
  const message: MessageId =
    reads.length === 2
      ? 'org.posthog.failed.both'
      : failed.funnel.isError
        ? 'org.posthog.failed.funnel'
        : 'org.posthog.failed.flags'
  // Trying again cannot bring back a read the API does not have (404, the kit's rule).
  const retryable = reads.some((q) => !isApiError(q.error, 404))
  return (
    <div className="ph-read-failed">
      <ErrorLine
        message={t(message)}
        retry={
          retryable
            ? () => {
                for (const q of reads) void q.refetch()
              }
            : undefined
        }
        retryLabel={t('state.retry')}
      />
    </div>
  )
}

export function OrgPostHog({ orgId }: { orgId: string }) {
  const { t, dateTime } = useI18n()
  const activity = useQuery(orgActivityQuery(orgId))
  const flags = useQuery(orgProductFlagsQuery(orgId))
  const failed = { funnel: activity, flags }
  return (
    <Section
      title={t('org.posthog.title')}
      right={<Link to="/product-flags">{t('org.posthog.openFlags')}</Link>}
    >
      {activity.isPending ? (
        <Loading />
      ) : activity.isError ? (
        <ReadFailed failed={failed} />
      ) : (
        <PostHogSource section={activity.data}>
          {activity.data.data ? (
            <KV
              items={[
                [t('org.posthog.step'), t(STEP[activity.data.data.step])],
                [
                  t('org.posthog.lastConsole'),
                  activity.data.data.last_console_at
                    ? dateTime(activity.data.data.last_console_at)
                    : t('org.posthog.never'),
                ],
              ]}
            />
          ) : null}
        </PostHogSource>
      )}
      {flags.isPending ? (
        <Loading />
      ) : flags.isError ? (
        // Already said by the funnel's line when both failed.
        activity.isError ? null : (
          <ReadFailed failed={failed} />
        )
      ) : (
        <PostHogSource section={flags.data} missing={t('flags.notConfigured')}>
          {flags.data.data && flags.data.data.length > 0 ? (
            <KV
              items={flags.data.data.map(
                (f) =>
                  [
                    <span key={f.id} translate="no" className="mono">
                      {f.key}
                    </span>,
                    <span key={`${f.id}-v`}>
                      <FlagState active={f.active} /> ·{' '}
                      {t(f.orgs.includes(orgId) ? 'org.posthog.named' : 'org.posthog.everyone')}
                    </span>,
                  ] as const,
              )}
            />
          ) : (
            <p className="dim">{t('org.posthog.noFlags')}</p>
          )}
        </PostHogSource>
      )}
    </Section>
  )
}
